// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CreateReportPage from './pages/CreateReportPage';
import ProtectedRoute from './components/ProtectedRoute';
import { initFreshContent, checkVersionAndReload } from './utils/cacheBuster';
import { authService } from './services/authService';

/**
 * Sends the user back to the login page the moment their authentication
 * lapses while they are on a protected page — token expired (the 500ms poll
 * in App flips `authed`), or the server rejected the token and the axios
 * interceptor cleared it (it also raises 'biomed:auth-expired').
 *
 * Only fires if the user WAS authenticated in this page session, so a fresh
 * visitor who types /dashboard is handled by ProtectedRoute as before and
 * does not get told their session expired.
 */
function AuthWatcher({ authed, onLapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const wasAuthed = React.useRef(false);

  useEffect(() => {
    if (authed) { wasAuthed.current = true; return; }
    if (!wasAuthed.current) return;
    if (location.pathname === '/') return;
    wasAuthed.current = false;
    authService.stopTokenRefresh();
    localStorage.removeItem('token');
    localStorage.removeItem('lastUsername');
    navigate('/', { replace: true, state: { reason: 'expired' } });
  }, [authed, location.pathname, navigate]);

  useEffect(() => {
    const onExpired = () => {
      if (location.pathname === '/') return;
      wasAuthed.current = false;
      authService.stopTokenRefresh();
      // Flip App's state in the SAME batch as the navigation. Left to the
      // 500ms poll, "/" would still see isAuthenticated === true for a tick,
      // <Navigate> back to /dashboard, whose guard would <Navigate> back here —
      // a render ping-pong that wedged the page on /dashboard.
      if (onLapse) onLapse();
      navigate('/', { replace: true, state: { reason: 'expired' } });
    };
    window.addEventListener('biomed:auth-expired', onExpired);
    return () => window.removeEventListener('biomed:auth-expired', onExpired);
  }, [location.pathname, navigate]);

  return null;
}

function App() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize fresh content loading on app startup
  useEffect(() => {
    initFreshContent();
    
    // Check for new version once on initial load only.
    // The periodic (every 5 min) check was removed because it hard-reloaded
    // the app mid-work, wiping in-progress report state and dropping the user
    // back on the dashboard. New versions are now picked up on the next load.
    checkVersionAndReload();
  }, []);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user is already authenticated
        if (authService.isAuthenticated()) {
          setIsAuthenticated(true);
        } else {
          // Try auto-login
          const autoLoginResult = await authService.attemptAutoLogin();
          if (autoLoginResult && autoLoginResult.token) {
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.log('Auth check failed:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();

    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      }
    };

    // Check authentication on navigation (for same-tab token changes)
    const checkAuthOnNavigation = () => {
      const hasToken = authService.isAuthenticated();
      setIsAuthenticated(hasToken);
    };

    // Check authentication periodically to catch same-tab changes
    const authCheckInterval = setInterval(checkAuthOnNavigation, 500);

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(authCheckInterval);
    };
  }, []);

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <AuthWatcher authed={isAuthenticated} onLapse={() => setIsAuthenticated(false)} />
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated && authService.isAuthenticated() ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/create-report" 
          element={
            <ProtectedRoute>
              <CreateReportPage />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;
